<div class="p-4 pt-0">
  <!-- Header -->
  <div class="page-heading mb-4 flex items-center justify-between">
    <div class="flex items-center gap-3">
      <button (click)="goBack()" class="btn-neutral px-2">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
      </button>
      <h3 class="text-2xl font-semibold text-neutral-800">Session #{{ sessionId }}</h3>
    </div>
    @if (session(); as s) {
      <div class="flex gap-3">
        @if (session()!.is_video_active) {
          <button (click)="joinAsObserver()" [disabled]="isJoining()" class="btn bg-blue-600 text-white px-6 py-2 rounded">
            @if (isJoining()) {
              Joining...
            } @else {
              Join as Hidden Observer
            }
          </button>
        }
        <button (click)="openBulkRefundDrawer()" [disabled]="!hasRefundableParticipants()"
          class="btn bg-red-50 text-red-600 border border-red-200 px-6 py-2 rounded disabled:opacity-40 disabled:cursor-not-allowed">
          Refund All Participants
        </button>
      </div>
    }
  </div>

  @if (isLoading()) {
    <div class="flex items-center justify-center py-20" [thkLibProcessing]="true"></div>
  } @else if (session()) {
    <!-- Tabs -->
    <div class="flex gap-1 border-b border-neutral-200 mb-6">
      <button (click)="switchTab('overview')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'overview'"
        [class.text-blue-600]="activeTab() === 'overview'"
        [class.border-transparent]="activeTab() !== 'overview'"
        [class.text-neutral-500]="activeTab() !== 'overview'">
        Overview
      </button>
      <button (click)="switchTab('participants')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'participants'"
        [class.text-blue-600]="activeTab() === 'participants'"
        [class.border-transparent]="activeTab() !== 'participants'"
        [class.text-neutral-500]="activeTab() !== 'participants'">
        Participants ({{ session()!.participants?.length || 0 }})
      </button>
      <button (click)="switchTab('notifications')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'notifications'"
        [class.text-blue-600]="activeTab() === 'notifications'"
        [class.border-transparent]="activeTab() !== 'notifications'"
        [class.text-neutral-500]="activeTab() !== 'notifications'">
        Notifications
      </button>
      <button (click)="switchTab('reminders')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'reminders'"
        [class.text-blue-600]="activeTab() === 'reminders'"
        [class.border-transparent]="activeTab() !== 'reminders'"
        [class.text-neutral-500]="activeTab() !== 'reminders'">
        Reminders
      </button>
      <button (click)="switchTab('refund-history')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'refund-history'"
        [class.text-blue-600]="activeTab() === 'refund-history'"
        [class.border-transparent]="activeTab() !== 'refund-history'"
        [class.text-neutral-500]="activeTab() !== 'refund-history'">
        Refund History
      </button>
      <button (click)="switchTab('recording')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'recording'"
        [class.text-blue-600]="activeTab() === 'recording'"
        [class.border-transparent]="activeTab() !== 'recording'"
        [class.text-neutral-500]="activeTab() !== 'recording'">
        Recording & Chat
      </button>
      <button (click)="switchTab('analytics')"
        class="px-4 py-2.5 text-sm font-medium border-b-2 -mb-px"
        [class.border-blue-600]="activeTab() === 'analytics'"
        [class.text-blue-600]="activeTab() === 'analytics'"
        [class.border-transparent]="activeTab() !== 'analytics'"
        [class.text-neutral-500]="activeTab() !== 'analytics'">
        Analytics
      </button>
    </div>

    <!-- Tab: Overview -->
    @if (activeTab() === 'overview') {
      <div class="grid grid-cols-2 gap-6">
        <!-- Session Info -->
        <div class="card rounded-lg border border-neutral-100 p-6">
          <h5 class="text-lg font-semibold mb-4">Session Info</h5>
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-neutral-500">Course</span>
              <span class="font-medium">{{ session()!.course_title }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Series</span>
              <span class="font-medium">{{ session()!.series_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Instructor</span>
              <span class="font-medium">{{ session()!.instructor_name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Date/Time</span>
              <span class="font-medium">{{ session()!.start_datetime | date:'MMM d, y, h:mm a' }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Duration</span>
              <span class="font-medium">{{ session()!.duration }} minutes</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Status</span>
              <span class="font-medium">{{ session()!.temporal_status }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Makeup Session</span>
              <span class="font-medium">{{ session()!.is_make_up_class ? 'Yes' : 'No' }}</span>
            </div>
          </div>
        </div>

        <!-- Video Status -->
        <div class="card rounded-lg border border-neutral-100 p-6">
          <h5 class="text-lg font-semibold mb-4">Video Status</h5>
          <div class="space-y-3">
            <div class="flex justify-between">
              <span class="text-neutral-500">Live</span>
              @if (session()!.is_video_active) {
                <span class="flex items-center gap-1.5">
                  <span class="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                  <span class="text-green-600 font-medium">Active</span>
                </span>
              } @else {
                <span class="text-neutral-400">Not Active</span>
              }
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Instructor Joined</span>
              @if (session()!.instructor_joined_at) {
                <span class="font-medium">{{ session()!.instructor_joined_at | date:'h:mm a' }}</span>
              } @else {
                <span class="text-neutral-400">-</span>
              }
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Actual Start</span>
              @if (session()!.actual_start_time) {
                <span class="font-medium">{{ session()!.actual_start_time | date:'h:mm a' }}</span>
              } @else {
                <span class="text-neutral-400">-</span>
              }
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Actual End</span>
              @if (session()!.actual_end_time) {
                <span class="font-medium">{{ session()!.actual_end_time | date:'h:mm a' }}</span>
              } @else {
                <span class="text-neutral-400">-</span>
              }
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Peak Participants</span>
              <span class="font-medium">{{ session()!.peak_participants }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-neutral-500">Enrolled</span>
              <span class="font-medium">{{ session()!.enrolled_count }}</span>
            </div>
            @if (session()!.instructor_late) {
              <div class="bg-amber-50 border border-amber-200 rounded p-3 mt-2">
                <span class="text-amber-700 text-sm font-medium">Instructor was late to this session</span>
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Tab: Participants -->
    @if (activeTab() === 'participants') {
      <div class="card rounded-lg border border-neutral-100">
        <div class="card-subheader flex items-center justify-between px-6 py-5">
          <h6>Participants</h6>
        </div>
        <div class="card-body">
          <div class="datagrid-table relative overflow-x-auto">
            <table class="w-full text-left">
              <thead class="heading">
                <tr>
                  <th scope="col"><div class="col-name">Student</div></th>
                  <th scope="col"><div class="col-name">Email</div></th>
                  <th scope="col"><div class="col-name">Status</div></th>
                  <th scope="col"><div class="col-name">Join Time</div></th>
                  <th scope="col"><div class="col-name">Duration</div></th>
                  <th scope="col"><div class="col-name">Refunded</div></th>
                  <th scope="col"><div class="col-name">Action</div></th>
                </tr>
              </thead>
              <tbody>
                @for (att of session()!.participants; track att.id) {
                  <tr>
                    <td>{{ att.student_name }}</td>
                    <td>{{ att.student_email }}</td>
                    <td>
                      <span class="tag tag-sm rounded-lg"
                        [class.tag-green]="att.effective_status === 'ATTENDED'"
                        [class.tag-blue]="att.effective_status === 'ENROLLED'"
                        [class.tag-yellow]="att.effective_status === 'LATE'"
                        [class.tag-red]="att.effective_status === 'MISSED'">
                        {{ att.effective_status }}
                      </span>
                    </td>
                    <td>
                      @if (att.first_join_time) {
                        {{ att.first_join_time | date:'h:mm a' }}
                      } @else {
                        <span class="text-neutral-400">-</span>
                      }
                    </td>
                    <td>{{ att.total_duration_minutes }} min</td>
                    <td>
                      @if (att.refunded) {
                        <span class="text-green-600 font-medium">Yes</span>
                      } @else {
                        <span class="text-neutral-400">No</span>
                      }
                    </td>
                    <td>
                      @if (!att.refunded) {
                        <button (click)="openRefundDrawer(att)" class="text-sm text-blue-600 underline hover:text-blue-800">
                          Refund
                        </button>
                      }
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="7" class="text-center py-8 text-neutral-400">No participants</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }

    <!-- Tab: Notifications -->
    @if (activeTab() === 'notifications') {
      <div class="card rounded-lg border border-neutral-100">
        <div class="card-subheader flex items-center justify-between px-6 py-5">
          <div class="flex items-center gap-1">
            <h6>Notifications</h6>
            <div class="tag tag-sm text-slate-600">{{ totalNotifications() }}</div>
          </div>
        </div>
        <div class="card-body relative">
          @if (isNotificationsLoading()) {
            <div class="flex items-center justify-center w-full h-full left-0 right-0 absolute bg-[rgba(255,255,255,0.5)] z-20"
              [thkLibProcessing]="true"></div>
          }
          <div class="datagrid-table relative overflow-x-auto">
            <table class="w-full text-left">
              <thead class="heading">
                <tr>
                  <th scope="col"><div class="col-name">Type</div></th>
                  <th scope="col"><div class="col-name">Channel</div></th>
                  <th scope="col"><div class="col-name">Recipient</div></th>
                  <th scope="col"><div class="col-name">Status</div></th>
                  <th scope="col"><div class="col-name">Error</div></th>
                  <th scope="col"><div class="col-name">Date</div></th>
                </tr>
              </thead>
              <tbody>
                @for (log of notifications(); track log.id) {
                  <tr>
                    <td>{{ log.notification_type }}</td>
                    <td><span class="uppercase text-xs font-medium">{{ log.channel }}</span></td>
                    <td>
                      <div>{{ log.recipient_name }}</div>
                      <div class="text-xs text-neutral-400">{{ log.recipient_email }}</div>
                    </td>
                    <td><span [class]="getStatusClass(log.status)">{{ log.status }}</span></td>
                    <td>
                      @if (log.error_message) {
                        <span class="text-red-500 text-xs">{{ log.error_message }}</span>
                      } @else {
                        <span class="text-neutral-400">-</span>
                      }
                    </td>
                    <td>{{ log.date_created | date:'MMM d, h:mm a' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="6" class="text-center py-8 text-neutral-400">No notifications</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="z-30 relative">
            <app-common-pagination
              (paginatorData)="notificationPage($event)"
              (pageSize)="notificationPageSize($event)"
              [config]="notificationPaginationConfig"
              [type]="'dynamic'">
            </app-common-pagination>
          </div>
        </div>
      </div>
    }

    <!-- Tab: Reminders -->
    @if (activeTab() === 'reminders') {
      <div class="card rounded-lg border border-neutral-100">
        <div class="card-subheader px-6 py-5">
          <h6>Reminders</h6>
        </div>
        <div class="card-body relative">
          @if (isRemindersLoading()) {
            <div class="flex items-center justify-center w-full h-full left-0 right-0 absolute bg-[rgba(255,255,255,0.5)] z-20"
              [thkLibProcessing]="true"></div>
          }
          <div class="datagrid-table relative overflow-x-auto">
            <table class="w-full text-left">
              <thead class="heading">
                <tr>
                  <th scope="col"><div class="col-name">Type</div></th>
                  <th scope="col"><div class="col-name">Recipient</div></th>
                  <th scope="col"><div class="col-name">Role</div></th>
                  <th scope="col"><div class="col-name">Status</div></th>
                  <th scope="col"><div class="col-name">Scheduled At</div></th>
                </tr>
              </thead>
              <tbody>
                @for (reminder of reminders(); track reminder.id) {
                  <tr>
                    <td>{{ reminder.reminder_type }}</td>
                    <td>
                      <div>{{ reminder.recipient_name }}</div>
                      <div class="text-xs text-neutral-400">{{ reminder.recipient_email }}</div>
                    </td>
                    <td><span class="capitalize">{{ reminder.recipient_role }}</span></td>
                    <td>
                      <div class="tag tag-sm rounded-lg {{ getReminderStatusClass(reminder.status) }}">
                        {{ reminder.status }}
                      </div>
                    </td>
                    <td>{{ reminder.scheduled_at | date:'MMM d, h:mm a' }}</td>
                  </tr>
                } @empty {
                  <tr>
                    <td colspan="5" class="text-center py-8 text-neutral-400">No reminders</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }

    <!-- Tab: Recording & Chat -->
    @if (activeTab() === 'recording') {
      <div class="grid grid-cols-3 gap-6">
        <!-- Recording Player (2 cols) -->
        <div class="col-span-2 space-y-4">
          <div class="card rounded-lg border border-neutral-100 p-6">
            <h5 class="text-lg font-semibold mb-4">Recording</h5>
            @if (isLoadingRecording()) {
              <div class="flex items-center justify-center py-12" [thkLibProcessing]="true"></div>
            } @else {
              @if (recordingInfo(); as info) {
                @if (!info.has_recording || info.recordings.length === 0) {
                  <div class="py-12 text-center text-sm text-neutral-400">No recording available for this session.</div>
                } @else {
                  <!-- Video Player -->
                  <div class="rounded-lg overflow-hidden bg-black mb-4">
                    <app-thk-video-player
                      [sources$]="videoSources$"
                      [settingsButton]="false"
                      [relatedMenu]="false"
                      [qualityMenu]="true"
                      [buttonRewind]="true"
                      [autoplay]="false"
                      [singlePlayer]="false"
                      [theaterButton]="false"
                      [poster]="''"
                      [lectureVideoCaptions]="[]"
                    ></app-thk-video-player>
                  </div>

                  <!-- Recording List (if multiple) -->
                  @if (info.recordings.length > 1) {
                    <div class="space-y-2">
                      <h6 class="text-sm font-medium text-neutral-600">All Recordings ({{ info.recording_count }})</h6>
                      @for (rec of info.recordings; track rec.id; let i = $index) {
                        <button (click)="playRecording(i)"
                          class="w-full text-left px-4 py-3 rounded-lg border border-neutral-200 hover:bg-neutral-50 flex items-center justify-between">
                          <div>
                            <span class="text-sm font-medium">Recording {{ i + 1 }}</span>
                            @if (rec.duration) {
                              <span class="text-xs text-neutral-400 ml-2">{{ formatMinutes(rec.duration / 60) }}</span>
                            }
                          </div>
                          <span class="tag tag-sm rounded-lg"
                            [class]="rec.status === 'completed' ? 'tag-green' : rec.status === 'processing' ? 'tag-yellow' : 'tag-neutral'">
                            {{ rec.status }}
                          </span>
                        </button>
                      }
                    </div>
                  } @else {
                    <!-- Single recording info -->
                    @if (info.recordings[0]; as rec) {
                      <div class="flex items-center gap-4 text-sm text-neutral-600">
                        <span class="tag tag-sm rounded-lg"
                          [class]="rec.status === 'completed' ? 'tag-green' : rec.status === 'processing' ? 'tag-yellow' : 'tag-neutral'">
                          {{ rec.status }}
                        </span>
                        @if (rec.duration) {
                          <span>Duration: {{ formatMinutes(rec.duration / 60) }}</span>
                        }
                        @if (rec.started_at) {
                          <span>Started: {{ rec.started_at | date:'h:mm a' }}</span>
                        }
                      </div>
                    }
                  }

                  <!-- Transcript -->
                  @if (info.recordings[0]?.transcript) {
                    <div class="mt-4">
                      <h6 class="text-sm font-medium text-neutral-600 mb-2">Transcript</h6>
                      <div class="bg-neutral-50 rounded-lg p-4 max-h-[300px] overflow-y-auto text-sm text-neutral-700 whitespace-pre-wrap">{{ info.recordings[0].transcript }}</div>
                    </div>
                  }
                }
              }
            }
          </div>
        </div>

        <!-- Chat Messages (1 col) -->
        <div class="col-span-1">
          <div class="card rounded-lg border border-neutral-100 p-6">
            <div class="flex items-center justify-between mb-4">
              <h5 class="text-lg font-semibold">Chat</h5>
              @if (totalChatMessages() > 0) {
                <span class="tag tag-sm text-slate-600">{{ totalChatMessages() }}</span>
              }
            </div>
            @if (isLoadingChat()) {
              <div class="flex items-center justify-center py-12" [thkLibProcessing]="true"></div>
            } @else if (chatMessages().length === 0) {
              <div class="py-12 text-center text-sm text-neutral-400">No chat messages.</div>
            } @else {
              <div class="space-y-3 max-h-[600px] overflow-y-auto">
                @for (msg of chatMessages(); track msg.id) {
                  <div class="flex gap-2">
                    <!-- Avatar -->
                    <div class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium"
                      [class]="msg.sender.is_instructor ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 text-neutral-600'">
                      {{ msg.sender.display_name?.charAt(0)?.toUpperCase() || '?' }}
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <span class="text-sm font-medium">{{ msg.sender.display_name }}</span>
                        @if (msg.sender.is_instructor) {
                          <span class="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded font-medium">Instructor</span>
                        }
                        @if (msg.is_pinned) {
                          <span class="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded font-medium">Pinned</span>
                        }
                        <span class="text-xs text-neutral-400">{{ msg.message_timestamp | date:'h:mm a' }}</span>
                      </div>
                      @if (msg.payload_type === 'TEXT_MESSAGE') {
                        <p class="text-sm text-neutral-700 mt-0.5 break-words">{{ msg.message_text }}</p>
                      } @else if (msg.payload_type === 'IMAGE_MESSAGE') {
                        <div class="mt-1">
                          @if (msg.file_info) {
                            <img [src]="msg.file_info.url" [alt]="msg.file_info.file_name"
                              class="max-w-[200px] rounded-lg border border-neutral-200" />
                          }
                          @if (msg.message_text) {
                            <p class="text-sm text-neutral-700 mt-1">{{ msg.message_text }}</p>
                          }
                        </div>
                      } @else if (msg.payload_type === 'FILE_MESSAGE') {
                        @if (msg.file_info) {
                          <a [href]="msg.file_info.url" target="_blank" rel="noopener"
                            class="inline-flex items-center gap-2 mt-1 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200 text-sm text-blue-600 hover:bg-neutral-100">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd" />
                            </svg>
                            {{ msg.file_info.file_name }}
                          </a>
                        }
                      }
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    <!-- Tab: Analytics -->
    @if (activeTab() === 'analytics') {
      @if (isLoadingAnalytics()) {
        <div class="flex items-center justify-center py-20" [thkLibProcessing]="true"></div>
      } @else {
        @if (analytics(); as data) {
        <!-- Session Info -->
        <div class="grid grid-cols-4 gap-4 mb-6">
          <div class="card rounded-lg border border-neutral-100 p-4">
            <div class="text-xs text-neutral-500 mb-1">Scheduled Duration</div>
            <div class="text-xl font-semibold">{{ formatMinutes(data.session.duration_minutes) }}</div>
          </div>
          <div class="card rounded-lg border border-neutral-100 p-4">
            <div class="text-xs text-neutral-500 mb-1">Actual Duration</div>
            <div class="text-xl font-semibold">{{ formatMinutes(data.session.actual_duration_minutes) }}</div>
          </div>
          <div class="card rounded-lg border border-neutral-100 p-4">
            <div class="text-xs text-neutral-500 mb-1">Actual Start</div>
            <div class="text-xl font-semibold">
              @if (data.session.actual_start_time) {
                {{ data.session.actual_start_time | date:'h:mm a' }}
              } @else {
                -
              }
            </div>
          </div>
          <div class="card rounded-lg border border-neutral-100 p-4">
            <div class="text-xs text-neutral-500 mb-1">Actual End</div>
            <div class="text-xl font-semibold">
              @if (data.session.actual_end_time) {
                {{ data.session.actual_end_time | date:'h:mm a' }}
              } @else {
                -
              }
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-6 mb-6">
          <!-- Attendance Stats -->
          <div class="card rounded-lg border border-neutral-100 p-6">
            <h5 class="text-lg font-semibold mb-4">Attendance</h5>
            <div class="grid grid-cols-3 gap-4 mb-4">
              <div class="text-center p-3 bg-blue-50 rounded-lg">
                <div class="text-2xl font-bold text-blue-700">{{ data.attendance.total_enrolled }}</div>
                <div class="text-xs text-blue-600">Enrolled</div>
              </div>
              <div class="text-center p-3 bg-green-50 rounded-lg">
                <div class="text-2xl font-bold text-green-700">{{ data.attendance.attended }}</div>
                <div class="text-xs text-green-600">Attended</div>
              </div>
              <div class="text-center p-3 bg-red-50 rounded-lg">
                <div class="text-2xl font-bold text-red-700">{{ data.attendance.missed }}</div>
                <div class="text-xs text-red-600">Missed</div>
              </div>
            </div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-neutral-500">Late Arrivals</span>
                <span class="font-medium">{{ data.attendance.late }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Excused</span>
                <span class="font-medium">{{ data.attendance.excused }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Recording Access</span>
                <span class="font-medium">{{ data.attendance.recording_access }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Attendance Rate</span>
                <span class="font-medium">{{ data.attendance.attendance_rate | number:'1.0-1' }}%</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Avg Duration</span>
                <span class="font-medium">{{ formatMinutes(data.attendance.avg_duration_minutes) }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Avg Session Coverage</span>
                <span class="font-medium">{{ data.attendance.avg_session_percentage | number:'1.0-1' }}%</span>
              </div>
            </div>
          </div>

          <!-- Participation Stats -->
          <div class="card rounded-lg border border-neutral-100 p-6">
            <h5 class="text-lg font-semibold mb-4">Participation</h5>
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div class="text-center p-3 bg-purple-50 rounded-lg">
                <div class="text-2xl font-bold text-purple-700">{{ data.participation.unique_participants }}</div>
                <div class="text-xs text-purple-600">Unique Participants</div>
              </div>
              <div class="text-center p-3 bg-amber-50 rounded-lg">
                <div class="text-2xl font-bold text-amber-700">{{ data.participation.peak_concurrent }}</div>
                <div class="text-xs text-amber-600">Peak Concurrent</div>
              </div>
            </div>
            <div class="space-y-2 text-sm">
              <div class="flex justify-between">
                <span class="text-neutral-500">Total Joins</span>
                <span class="font-medium">{{ data.participation.total_joins }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-neutral-500">Avg Duration</span>
                <span class="font-medium">{{ formatMinutes(data.participation.avg_duration_minutes) }}</span>
              </div>
            </div>

            <!-- Join Distribution Chart (simple bar) -->
            @if (data.timeline.join_distribution.length > 0) {
              <div class="mt-6">
                <h6 class="text-sm font-medium text-neutral-600 mb-3">Join Distribution (5-min buckets)</h6>
                <div class="flex items-end gap-1 h-24">
                  @for (bucket of data.timeline.join_distribution; track bucket.minute) {
                    <div class="flex-1 flex flex-col items-center gap-1">
                      <div class="w-full bg-blue-400 rounded-t"
                        [style.height.%]="(bucket.count / data.participation.peak_concurrent) * 100"
                        [title]="'+' + bucket.minute + 'min: ' + bucket.count + ' joins'">
                      </div>
                      <span class="text-[9px] text-neutral-400">{{ bucket.minute }}m</span>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>

        <!-- Provider Metrics -->
        @if (data.provider_metrics) {
          <div class="card rounded-lg border border-neutral-100 p-6">
            <h5 class="text-lg font-semibold mb-4">Technical Metrics</h5>
            <div class="grid grid-cols-3 gap-6">
              <!-- Devices -->
              @if (data.provider_metrics.device_summary) {
                <div>
                  <h6 class="text-sm font-medium text-neutral-600 mb-2">Devices</h6>
                  <div class="space-y-1.5">
                    @for (entry of data.provider_metrics.device_summary | keyvalue; track entry.key) {
                      <div class="flex justify-between text-sm">
                        <span class="text-neutral-500 capitalize">{{ entry.key }}</span>
                        <span class="font-medium">{{ entry.value }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
              <!-- Browsers -->
              @if (data.provider_metrics.browser_summary) {
                <div>
                  <h6 class="text-sm font-medium text-neutral-600 mb-2">Browsers</h6>
                  <div class="space-y-1.5">
                    @for (entry of data.provider_metrics.browser_summary | keyvalue; track entry.key) {
                      <div class="flex justify-between text-sm">
                        <span class="text-neutral-500 capitalize">{{ entry.key }}</span>
                        <span class="font-medium">{{ entry.value }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
              <!-- Countries -->
              @if (data.provider_metrics.country_summary) {
                <div>
                  <h6 class="text-sm font-medium text-neutral-600 mb-2">Countries</h6>
                  <div class="space-y-1.5">
                    @for (entry of data.provider_metrics.country_summary | keyvalue; track entry.key) {
                      <div class="flex justify-between text-sm">
                        <span class="text-neutral-500">{{ entry.key }}</span>
                        <span class="font-medium">{{ entry.value }}</span>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>
            @if (data.provider_metrics.avg_audio_quality || data.provider_metrics.avg_video_quality) {
              <div class="flex gap-6 mt-4 pt-4 border-t border-neutral-100">
                @if (data.provider_metrics.avg_audio_quality) {
                  <div class="text-sm">
                    <span class="text-neutral-500">Avg Audio Quality:</span>
                    <span class="font-medium ml-1">{{ data.provider_metrics.avg_audio_quality | number:'1.0-1' }}</span>
                  </div>
                }
                @if (data.provider_metrics.avg_video_quality) {
                  <div class="text-sm">
                    <span class="text-neutral-500">Avg Video Quality:</span>
                    <span class="font-medium ml-1">{{ data.provider_metrics.avg_video_quality | number:'1.0-1' }}</span>
                  </div>
                }
              </div>
            }
          </div>
        }
        } @else {
          <div class="py-12 text-center text-sm text-neutral-400">No analytics data available.</div>
        }
      }
    }

    <!-- Refund History Tab -->
    @if (activeTab() === 'refund-history') {
    <div class="card rounded-lg border border-neutral-200">
      <div class="p-6">
        @if (isLoadingRefundHistory()) {
          <div class="flex items-center justify-center py-12" [thkLibProcessing]="true"></div>
        } @else if (refundHistory().length === 0) {
          <div class="py-12 text-center text-sm text-neutral-400">No refunds issued for this session.</div>
        } @else {
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="border-b text-left text-xs text-neutral-500 uppercase">
                <tr>
                  <th class="px-4 py-2">Date</th>
                  <th class="px-4 py-2">Student</th>
                  <th class="px-4 py-2">Reason</th>
                  <th class="px-4 py-2">Amount</th>
                  <th class="px-4 py-2">Suggested</th>
                  <th class="px-4 py-2">Mode</th>
                  <th class="px-4 py-2">Status</th>
                  <th class="px-4 py-2">Admin</th>
                  <th class="px-4 py-2">Notes</th>
                  <th class="px-4 py-2">Instructor Note</th>
                </tr>
              </thead>
              <tbody>
                @for (entry of refundHistory(); track entry.id) {
                <tr class="border-b border-neutral-100">
                  <td class="px-4 py-2 text-neutral-600">{{ entry.date_created | date:'MMM d, yyyy HH:mm' }}</td>
                  <td class="px-4 py-2">{{ entry.student_name }}</td>
                  <td class="px-4 py-2">
                    <span class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium bg-neutral-100 text-neutral-700">
                      {{ entry.reason_display }}
                    </span>
                  </td>
                  <td class="px-4 py-2 font-medium">{{ entry.currency }} {{ entry.refund_amount }}</td>
                  <td class="px-4 py-2 text-neutral-400">
                    @if (entry.suggested_amount && entry.suggested_amount !== entry.refund_amount) {
                      {{ entry.currency }} {{ entry.suggested_amount }}
                    } @else {
                      -
                    }
                  </td>
                  <td class="px-4 py-2">
                    <span class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                      [class]="entry.refund_mode === 'credit' ? 'bg-blue-50 text-blue-700' : entry.refund_mode === 'credit_clawback' ? 'bg-amber-50 text-amber-700' : 'bg-purple-50 text-purple-700'">
                      {{ entry.refund_mode === 'credit' ? 'Credits (Platform)' : entry.refund_mode === 'credit_clawback' ? 'Credits (Instructor)' : 'Gateway' }}
                    </span>
                  </td>
                  <td class="px-4 py-2">
                    <span class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                      [class]="entry.status === 'processed' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'">
                      {{ entry.status }}
                    </span>
                  </td>
                  <td class="px-4 py-2 text-neutral-600">{{ entry.initiated_by_name }}</td>
                  <td class="px-4 py-2 text-neutral-500 max-w-[200px] truncate">{{ entry.notes || '-' }}</td>
                  <td class="px-4 py-2 text-neutral-500 max-w-[200px] truncate">{{ entry.instructor_note || '-' }}</td>
                </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
    }
  }
</div>

<!-- Refund Drawer -->
<app-drawer #refundDrawer>
  <ng-container header>
    <div class="px-6 py-5 bg-neutral-50 flex justify-between items-center border-b border-neutral-100">
      <h3 class="text-xl text-black font-semibold">Refund Participant</h3>
      <button (click)="refundDrawer.close()" class="bg-neutral-100 rounded w-6 h-6 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </ng-container>
  <ng-container content>
    @if (selectedAttendance) {
      <div class="p-6 space-y-6">
        <!-- Loading -->
        @if (isLoadingContext()) {
          <div class="flex justify-center py-8">
            <div class="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-blue-600"></div>
          </div>
        } @else {
        @if (purchaseContext(); as ctx) {
          <!-- Student Info -->
          <div class="rounded-lg border border-neutral-200 p-4">
            <h4 class="text-sm font-semibold text-neutral-700 mb-3">Student</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <span class="text-neutral-500">Name</span>
              <span class="font-medium">{{ ctx.student_name }}</span>
              <span class="text-neutral-500">Email</span>
              <span class="font-medium">{{ ctx.student_email }}</span>
              <span class="text-neutral-500">Credit Balance</span>
              <span class="font-medium text-emerald-600">{{ ctx.original_currency }} {{ ctx.student_credit_balance }}</span>
            </div>
          </div>

          <!-- Original Purchase -->
          <div class="rounded-lg border border-neutral-200 p-4">
            <h4 class="text-sm font-semibold text-neutral-700 mb-3">Original Purchase</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <span class="text-neutral-500">Amount Paid</span>
              <span class="font-medium">{{ ctx.original_currency }} {{ ctx.original_payment_amount }}</span>
              <span class="text-neutral-500">Payment Method</span>
              <span class="font-medium capitalize">{{ ctx.payment_method }}</span>
              <span class="text-neutral-500">Sessions Included</span>
              <span class="font-medium">{{ ctx.total_sessions_in_series }}</span>
              <span class="text-neutral-500">Per Session Value</span>
              <span class="font-medium">{{ ctx.original_currency }} {{ ctx.per_session_amount }}</span>
            </div>
          </div>

          <!-- Purchased Sessions -->
          @if (purchaseContext()?.purchased_sessions?.length) {
          <details class="rounded-lg border border-neutral-200">
            <summary class="px-4 py-3 cursor-pointer text-sm font-semibold text-neutral-700 bg-neutral-50 rounded-t-lg">
              Purchased Sessions ({{ purchaseContext()!.purchased_session_count }})
            </summary>
            <div class="px-4 py-2 max-h-[150px] overflow-y-auto">
              @for (s of purchaseContext()!.purchased_sessions; track s.session_id) {
                <div class="flex justify-between text-xs py-1 border-b border-neutral-50">
                  <span class="text-neutral-500">#{{ s.session_id }}</span>
                  <span>{{ s.start_datetime | date:'MMM d, yyyy HH:mm' }}</span>
                </div>
              }
            </div>
          </details>
          }

          @if (purchaseContext()?.added_later_sessions?.length) {
          <details class="rounded-lg border border-neutral-200">
            <summary class="px-4 py-3 cursor-pointer text-sm font-semibold text-neutral-700 bg-neutral-50 rounded-t-lg">
              Added Later — Not Paid For ({{ purchaseContext()!.added_later_sessions!.length }})
            </summary>
            <div class="px-4 py-2 max-h-[150px] overflow-y-auto">
              @for (s of purchaseContext()!.added_later_sessions; track s.session_id) {
                <div class="flex justify-between text-xs py-1 border-b border-neutral-50">
                  <span class="text-neutral-500">#{{ s.session_id }}</span>
                  <span>{{ s.start_datetime | date:'MMM d, yyyy HH:mm' }}</span>
                </div>
              }
            </div>
          </details>
          }

          <!-- Refund Status -->
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <h4 class="text-sm font-semibold text-amber-800 mb-3">Refund Status</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <span class="text-amber-700">Sessions Refunded</span>
              <span class="font-medium">{{ ctx.sessions_already_refunded }} / {{ ctx.total_sessions_in_series }}</span>
              <span class="text-amber-700">Total Refunded</span>
              <span class="font-medium">{{ ctx.original_currency }} {{ ctx.total_already_refunded }}</span>
              <span class="text-amber-700">This Session</span>
              <span class="font-medium">
                @if (ctx.is_this_session_refunded) {
                  <span class="text-red-600">Already Refunded</span>
                } @else {
                  <span class="text-green-600">Not Refunded</span>
                }
              </span>
            </div>
          </div>

          <!-- Instructor History -->
          @if (purchaseContext()?.instructor_stats) {
          <div class="rounded-lg border border-neutral-200 p-4">
            <h4 class="text-sm font-semibold text-neutral-700 mb-3">Instructor History</h4>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <span class="text-neutral-500">No-shows this series</span>
              <span class="font-medium">{{ purchaseContext()!.instructor_stats!.no_shows_this_series }} / {{ purchaseContext()!.instructor_stats!.total_sessions_this_series }}</span>
              <span class="text-neutral-500">No-shows this course</span>
              <span class="font-medium">{{ purchaseContext()!.instructor_stats!.no_shows_this_course }} / {{ purchaseContext()!.instructor_stats!.total_sessions_this_course }} ({{ purchaseContext()!.instructor_stats!.no_show_rate_this_course }}%)</span>
              <span class="text-neutral-500">No-shows global</span>
              <span class="font-medium">{{ purchaseContext()!.instructor_stats!.no_shows_global }} / {{ purchaseContext()!.instructor_stats!.total_sessions_global }} ({{ purchaseContext()!.instructor_stats!.no_show_rate_global }}%)</span>
            </div>
          </div>
          }

          <!-- Student Refund Pattern -->
          @if (purchaseContext()?.student_stats) {
          <div class="rounded-lg border p-4"
            [class]="purchaseContext()!.student_stats!.risk_level === 'high' ? 'border-red-200 bg-red-50' : purchaseContext()!.student_stats!.risk_level === 'elevated' ? 'border-amber-200 bg-amber-50' : 'border-neutral-200'">
            <div class="flex items-center justify-between mb-3">
              <h4 class="text-sm font-semibold text-neutral-700">Student Refund Pattern</h4>
              @if (purchaseContext()!.student_stats!.risk_level !== 'normal') {
                <span class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium"
                  [class]="purchaseContext()!.student_stats!.risk_level === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'">
                  {{ purchaseContext()!.student_stats!.risk_level === 'high' ? 'HIGH RISK' : 'ELEVATED' }}
                </span>
              }
            </div>
            <div class="grid grid-cols-2 gap-2 text-sm">
              <span class="text-neutral-500">Refunds this course</span>
              <span class="font-medium">{{ purchaseContext()!.student_stats!.session_refunds_this_course }}</span>
              <span class="text-neutral-500">Refunds global</span>
              <span class="font-medium">{{ purchaseContext()!.student_stats!.session_refunds_global }} session + {{ purchaseContext()!.student_stats!.full_booking_refunds_global }} full-booking</span>
              <span class="text-neutral-500">Courses purchased</span>
              <span class="font-medium">{{ purchaseContext()!.student_stats!.total_course_purchases }}</span>
              <span class="text-neutral-500">Sessions enrolled</span>
              <span class="font-medium">{{ purchaseContext()!.student_stats!.total_sessions_enrolled }}</span>
              <span class="text-neutral-500">Session refund rate</span>
              <span class="font-medium">{{ purchaseContext()!.student_stats!.refund_rate }}%</span>
              <span class="text-neutral-500">Total refunded</span>
              <span class="font-medium">{{ purchaseContext()!.original_currency }} {{ purchaseContext()!.student_stats!.total_refunded_amount }}</span>
            </div>
          </div>
          }

          <!-- Refund Form (only if not already refunded) -->
          @if (!ctx.is_this_session_refunded) {
          <div class="rounded-lg border border-neutral-200 p-4 space-y-4">
            <h4 class="text-sm font-semibold text-neutral-700">Issue Refund</h4>

            <!-- Reason -->
            <div>
              <label class="block text-sm text-neutral-600 mb-1">Refund Reason</label>
              <select [(ngModel)]="selectedReason"
                class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
                @for (r of refundReasons(); track r.value) {
                  <option [value]="r.value">{{ r.label }}</option>
                }
              </select>
              @if (selectedReason === 'OTHER') {
                <input type="text" [(ngModel)]="reasonOther" placeholder="Specify reason..."
                  class="w-full mt-2 rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              }
            </div>

            <!-- Amount -->
            <div>
              <label class="block text-sm text-neutral-600 mb-1">
                Refund Amount
                <span class="text-neutral-400 text-xs ml-1">(suggested: {{ ctx.per_session_amount }})</span>
              </label>
              <div class="flex items-center gap-2 mb-2">
                <label class="flex items-center gap-1 text-sm cursor-pointer">
                  <input type="checkbox" [checked]="useCustomAmount" (change)="useCustomAmount = !useCustomAmount"
                    class="rounded border-neutral-300" />
                  Custom amount
                </label>
              </div>
              @if (useCustomAmount) {
                <input type="number" [(ngModel)]="customRefundAmount" min="0.01"
                  step="0.01"
                  class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
              }
            </div>

            <!-- Mode -->
            <div>
              <label class="block text-sm text-neutral-600 mb-1">Refund Method</label>
              <select [(ngModel)]="customRefundMode"
                class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
                <option value="credit">Thkee Credits</option>
                @if (ctx.gateway_refund_available) {
                  <option value="gateway">{{ ctx.gateway_name }} (Credit Card)</option>
                }
              </select>
            </div>

            <!-- Platform absorbs toggle -->
            <div class="flex items-center justify-between">
              <div>
                <label class="text-sm text-neutral-600">Platform absorbs cost</label>
                <p class="text-xs text-neutral-400">If unchecked, instructor's share is deducted</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" [(ngModel)]="platformAbsorbs" class="sr-only peer" />
                <div class="w-9 h-5 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"></div>
              </label>
            </div>

            <!-- Full instructor clawback toggle (only when NOT platform absorbs) -->
            @if (!platformAbsorbs) {
            <div class="flex items-center justify-between">
              <div>
                <label class="text-sm text-neutral-600">Full instructor deduction</label>
                <p class="text-xs text-neutral-400">Instructor pays the full refund amount, not just their share</p>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" [(ngModel)]="fullInstructorClawback" class="sr-only peer" />
                <div class="w-9 h-5 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:bg-red-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"></div>
              </label>
            </div>
            }

            <!-- Notes -->
            <div>
              <label class="block text-sm text-neutral-600 mb-1">Admin Notes</label>
              <textarea [(ngModel)]="refundNotes" rows="2"
                class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                placeholder="Reason for refund..."></textarea>
            </div>

            <!-- Instructor Note (only when clawback will happen) -->
            @if (customRefundMode === 'credit' && !platformAbsorbs) {
            <div>
              <label class="block text-sm text-neutral-600 mb-1">Note to Instructor</label>
              <p class="text-xs text-neutral-400 mb-1">Visible to instructor in their payout page</p>
              <textarea [(ngModel)]="instructorNote" rows="2"
                class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
                placeholder="e.g. Instructor no-show on session #123, 2nd occurrence..."></textarea>
            </div>
            }

            <!-- Submit -->
            <button (click)="processRefund()" [disabled]="isRefunding()"
              class="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              @if (isRefunding()) {
                Processing...
              } @else {
                Process Refund
              }
            </button>
          </div>
          }

          <!-- Refund Result -->
          @if (refundResult(); as result) {
          <div class="rounded-lg border border-green-200 bg-green-50 p-4 space-y-1">
            <p class="text-sm font-medium text-green-800">
              Refund processed: {{ result.currency }} {{ result.refund_amount }}
              ({{ result.refund_mode_display || result.refund_mode }})
            </p>
            @if (result.suggested_amount && result.suggested_amount !== result.refund_amount) {
              <p class="text-xs text-green-600">Suggested amount was: {{ result.currency }} {{ result.suggested_amount }}</p>
            }
            <p class="text-xs text-green-600">
              New credit balance: {{ result.student_credit_balance }}
            </p>
          </div>
          }

          <!-- Refund Error -->
          @if (refundError(); as error) {
          <div class="rounded-lg border border-red-200 bg-red-50 p-4">
            <p class="text-sm text-red-700">{{ error }}</p>
          </div>
          }
        }
        }
      </div>
    }
  </ng-container>
</app-drawer>

<!-- Bulk Refund Confirmation Drawer -->
<app-drawer #bulkRefundDrawer>
  <ng-container header>
    <div class="px-6 py-5 bg-red-50 flex justify-between items-center border-b border-red-100">
      <h3 class="text-xl text-red-800 font-semibold">Refund All Participants</h3>
      <button (click)="bulkRefundDrawer.close()" class="bg-red-100 rounded w-6 h-6 flex items-center justify-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-3 h-3 text-red-600" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>
  </ng-container>
  <ng-container content>
    <div class="p-6 space-y-6">
      <!-- Error loading preview -->
      @if (bulkRefundError(); as error) {
        <div class="rounded-lg border border-red-200 bg-red-50 p-4">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>
      }

      @if (isLoadingBulkPreview()) {
        <div class="flex justify-center py-8">
          <div class="h-6 w-6 animate-spin rounded-full border-2 border-neutral-300 border-t-red-600"></div>
        </div>
      } @else if (bulkRefundPreview() && bulkRefundPreview()!.participant_count === 0) {
        <div class="rounded-lg border border-green-200 bg-green-50 p-4">
          <p class="text-sm font-medium text-green-800">All participants have already been refunded for this session.</p>
        </div>
      } @else if (bulkRefundPreview()) {

        <!-- Warning Banner -->
        <div class="rounded-lg border border-red-200 bg-red-50 p-4">
          <p class="text-sm font-medium text-red-800">
            This will refund <strong>{{ bulkRefundPreview()!.participant_count }}</strong> participant(s)
            for a total of <strong>{{ bulkRefundPreview()!.currency }} {{ bulkRefundPreview()!.total_refund_amount }}</strong>
          </p>
        </div>

        <!-- Participants List -->
        <div class="rounded-lg border border-neutral-200">
          <div class="px-4 py-3 border-b border-neutral-100 bg-neutral-50">
            <h4 class="text-sm font-semibold text-neutral-700">Affected Participants</h4>
          </div>
          <div class="max-h-[200px] overflow-y-auto">
            <table class="w-full text-sm">
              <thead class="border-b text-left text-xs text-neutral-500 uppercase sticky top-0 bg-white">
                <tr>
                  <th class="px-4 py-2">Student</th>
                  <th class="px-4 py-2 text-right">Amount</th>
                  <th class="px-4 py-2">Paid Via</th>
                </tr>
              </thead>
              <tbody>
                @for (p of bulkRefundPreview()!.participants; track p.attendance_id) {
                  @if (!p.already_refunded) {
                  <tr class="border-b border-neutral-50">
                    <td class="px-4 py-2">
                      <div class="font-medium">{{ p.student_name }}</div>
                      <div class="text-xs text-neutral-400">{{ p.student_email }}</div>
                    </td>
                    <td class="px-4 py-2 text-right font-medium">{{ p.currency }} {{ p.per_session_amount }}</td>
                    <td class="px-4 py-2 text-neutral-500">{{ p.payment_method_display }}</td>
                  </tr>
                  }
                }
              </tbody>
            </table>
          </div>
        </div>

        @if (!bulkRefundSuccess()) {
        <!-- Refund Options -->
        <div class="rounded-lg border border-neutral-200 p-4 space-y-4">
          <!-- Reason -->
          <div>
            <label class="block text-sm text-neutral-600 mb-1">Refund Reason</label>
            <select [(ngModel)]="bulkSelectedReason"
              class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
              @for (r of refundReasons(); track r.value) {
                <option [value]="r.value">{{ r.label }}</option>
              }
            </select>
            @if (bulkSelectedReason === 'OTHER') {
              <input type="text" [(ngModel)]="bulkReasonOther" placeholder="Specify reason..."
                class="w-full mt-2 rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400" />
            }
          </div>

          <!-- Refund Method -->
          <div>
            <label class="block text-sm text-neutral-600 mb-1">Refund Method</label>
            <select [(ngModel)]="bulkRefundMode"
              class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400">
              <option value="credit">Thkee Credits</option>
              <option value="gateway">Original Payment Method (Credit Card)</option>
            </select>
          </div>

          <!-- Platform absorbs toggle -->
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm text-neutral-600">Platform absorbs cost</label>
              <p class="text-xs text-neutral-400">If unchecked, instructor's share is deducted</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" [(ngModel)]="bulkPlatformAbsorbs" class="sr-only peer" />
              <div class="w-9 h-5 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"></div>
            </label>
          </div>

          <!-- Full instructor clawback toggle (only when NOT platform absorbs) -->
          @if (!bulkPlatformAbsorbs) {
          <div class="flex items-center justify-between">
            <div>
              <label class="text-sm text-neutral-600">Full instructor deduction</label>
              <p class="text-xs text-neutral-400">Instructor pays the full refund amount, not just their share</p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" [(ngModel)]="fullInstructorClawback" class="sr-only peer" />
              <div class="w-9 h-5 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:bg-red-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full"></div>
            </label>
          </div>
          }

          <!-- Notes -->
          <div>
            <label class="block text-sm text-neutral-600 mb-1">Admin Notes</label>
            <textarea [(ngModel)]="bulkRefundNotes" rows="2"
              class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Additional notes (optional)..."></textarea>
          </div>

          <!-- Instructor Note (only when clawback will happen) -->
          @if (bulkRefundMode === 'credit' && !bulkPlatformAbsorbs) {
          <div>
            <label class="block text-sm text-neutral-600 mb-1">Note to Instructor</label>
            <p class="text-xs text-neutral-400 mb-1">Visible to instructor in their payout page</p>
            <textarea [(ngModel)]="instructorNote" rows="2"
              class="w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:border-blue-400"
              placeholder="Reason for payout deduction..."></textarea>
          </div>
          }

          <!-- CONFIRM input (gateway + >3 participants) -->
          @if (requiresConfirmInput) {
          <div class="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-2">
            <p class="text-sm text-amber-800 font-medium">
              You are about to refund <strong>{{ bulkRefundPreview()!.participant_count }}</strong> participants
              via their original payment method. This action cannot be undone.
            </p>
            <label class="block text-sm text-amber-700">
              Type <strong>CONFIRM</strong> to proceed:
            </label>
            <input type="text" [(ngModel)]="bulkConfirmInput"
              class="w-full rounded-md border border-amber-300 px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
              placeholder="CONFIRM" autocomplete="off" />
          </div>
          }

          <!-- Submit -->
          <button (click)="confirmBulkRefund()"
            [disabled]="isBulkRefunding() || !canSubmitBulkRefund"
            class="w-full rounded-md px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            [class]="canSubmitBulkRefund ? 'bg-red-600 hover:bg-red-700' : 'bg-neutral-400'">
            @if (isBulkRefunding()) {
              Processing...
            } @else {
              Confirm Refund All ({{ bulkRefundPreview()!.currency }} {{ bulkRefundPreview()!.total_refund_amount }})
            }
          </button>
        </div>
        }

        <!-- Success -->
        @if (bulkRefundSuccess()) {
        <div class="rounded-lg border border-green-200 bg-green-50 p-4">
          <p class="text-sm font-medium text-green-800">
            Bulk refund processed successfully for {{ bulkRefundPreview()!.participant_count }} participant(s).
          </p>
        </div>
        }

        <!-- Error -->
        @if (bulkRefundError(); as error) {
        <div class="rounded-lg border border-red-200 bg-red-50 p-4">
          <p class="text-sm text-red-700">{{ error }}</p>
        </div>
        }
      }
    </div>
  </ng-container>
</app-drawer>
